// @aiContributed-2025-11-04
import _ from 'underscore';
import { parse } from '@bingads-webui/odata-bulk-upsert';
import type {
  BulkUpsertErrorSummary,
  BulkUpsertLogEntity,
  BulkUpsertResponseLog,
  BulkUpsertSummary,
} from '../../types';

export const responseAttributesToLog = ['Id', 'Type'];
export const errorAttributesToLog = ['Error', 'ErrorNumber', 'Id', 'Property', 'Type'];

// Helper function to split CSV row while respecting quoted values
const splitCsvRow = (row: string) => {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < row.length; i += 1) {
    const char = row[i];
    const nextChar = row[i + 1];

    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        // Escaped quote ("")
        current += char;
        i += 1; // Skip next quote
      } else {
        // Toggle quote state
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      // End of field
      result.push(current);
      current = '';
    } else {
      current += char;
    }
  }
  // Add last field
  result.push(current);
  return result;
};

export const isBulkUpsert = (url: string) => /\/Customers\(\d+\)\/Accounts\(\d+\)\/.*Default\.BulkUpsert/.test(url);

// Helper function to summarize entities by Type
const summarizeByType = (entities: BulkUpsertLogEntity[]) => {
  const summary: BulkUpsertSummary[] = [];
  const groupedByType = _.groupBy(entities, 'Type');

  _.each(groupedByType, (items: BulkUpsertLogEntity[], type: string) => {
    const ids = _.pluck(items, 'Id');
    summary.push({
      Type: type,
      Count: ids.length,
      Ids: ids.slice(0, 10),
    });
  });

  return summary;
};

// Helper function to summarize errors by multiple attributes (Error, ErrorNumber, Property, Type)
const summarizeByErrorAttributes = (errors: BulkUpsertLogEntity[]) => {
  const summary: BulkUpsertErrorSummary[] = [];

  // Group by Error, ErrorNumber, Property, and Type (excluding Id)
  const grouped = _.groupBy(errors, (error: BulkUpsertLogEntity) => {
    const key = `${error.Error}|${error.ErrorNumber}|${error.Property}|${error.Type}`;
    return key;
  });

  _.each(grouped, (items: BulkUpsertLogEntity[]) => {
    const ids = _.pluck(items, 'Id');
    const firstItem = items[0];
    summary.push({
      Error: firstItem.Error,
      ErrorNumber: firstItem.ErrorNumber,
      Property: firstItem.Property,
      Type: firstItem.Type,
      Count: ids.length,
      Ids: ids.slice(0, 10),
    });
  });

  return summary;
};

export const getRequestDataToLog = (requestData: string) => {
  const json = JSON.parse(requestData) as { CsvRows: string[] };
  const headerRow = json.CsvRows[0];
  const headers = headerRow.split(',');
  let idPos = -1;
  let typePos = -1;

  for (let i = 0; i < headers.length; i += 1) {
    if (headers[i] === 'Id') {
      idPos = i;
    }

    if (headers[i] === 'Type') {
      typePos = i;
    }

    if (idPos !== -1 && typePos !== -1) {
      break;
    }
  }

  // row 0 is header, row 1 is type
  const dataRows = json.CsvRows.slice(2);

  const updatedEntities: BulkUpsertLogEntity[] = [];
  _.each(dataRows, (row: string) => {
    const rowValues = splitCsvRow(row);
    updatedEntities.push({ Id: rowValues[idPos], Type: rowValues[typePos] });
  });

  return summarizeByType(updatedEntities);
};

export const getResponseDataToLog = (data: unknown): BulkUpsertResponseLog => {
  const { results = [], errors = [] } = parse(data);

  // Filter out results with ' Error' in Type field
  const filteredResults = results
    .map(result => _.pick(result, responseAttributesToLog))
    .filter(result => !(result.Type && (result.Type as string).includes(' Error')));

  const resultsSummary = summarizeByType(filteredResults);

  // Group errors by multiple attributes and summarize
  const filteredErrors = errors.map(error => _.pick(error, errorAttributesToLog));
  const errorsSummary = summarizeByErrorAttributes(filteredErrors);

  return { Results: resultsSummary, Errors: errorsSummary };
};

